import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { PERMISSION_RESOURCES, PERMISSION_ROLES, defaultPermissionMatrix } from '../utils/permissionCatalog';
import './PermissionsPage.css';

const LEVELS = [
  { value: 'none', label: 'Oculto', shortLabel: 'O' },
  { value: 'read', label: 'Lectura', shortLabel: 'L' },
  { value: 'write', label: 'Escritura', shortLabel: 'E' },
];

function groupResources(resources) {
  return resources.reduce((groups, resource) => {
    const group = groups.find(item => item.module === resource.module);
    if (group) {
      group.resources.push(resource);
    } else {
      groups.push({ module: resource.module, resources: [resource] });
    }
    return groups;
  }, []);
}

export default function PermissionsPage() {
  const [roles, setRoles] = useState([]);
  const [resources, setResources] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [dirtyRoles, setDirtyRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [apiReady, setApiReady] = useState(true);

  useEffect(() => {
    api.get('/permissions')
      .then(({ data }) => {
        const nextRoles = data.roles?.length ? data.roles : PERMISSION_ROLES;
        const nextResources = data.resources?.length ? data.resources : PERMISSION_RESOURCES;
        setRoles(nextRoles);
        setResources(nextResources);
        setMatrix(Object.keys(data.matrix || {}).length ? data.matrix : defaultPermissionMatrix());
        setApiReady(true);
      })
      .catch(err => {
        setRoles(PERMISSION_ROLES);
        setResources(PERMISSION_RESOURCES);
        setMatrix(defaultPermissionMatrix());
        setApiReady(false);
        const status = err.response?.status ? `HTTP ${err.response.status}` : 'sin respuesta';
        setError(`No se pudo conectar con la API de permisos (${status}). Se muestra la matriz por defecto, pero no se puede guardar hasta actualizar el backend.`);
      })
      .finally(() => setLoading(false));
  }, []);

  const groupedResources = useMemo(() => groupResources(resources), [resources]);

  const setPermission = (roleKey, resourceKey, accessLevel) => {
    setMatrix(prev => ({
      ...prev,
      [roleKey]: {
        ...(prev[roleKey] || {}),
        [resourceKey]: accessLevel,
      },
    }));
    setDirtyRoles(prev => ({ ...prev, [roleKey]: true }));
    setSaved(false);
  };

  const saveChanges = async () => {
    if (!apiReady) return;
    const pendingRoles = roles.filter(role => !role.locked && dirtyRoles[role.key]);
    if (!pendingRoles.length) return;
    setSaving(true);
    setError('');
    try {
      const results = await Promise.allSettled(pendingRoles.map(async role => {
        const { data } = await api.put(`/permissions/${role.key}`, {
          permissions: matrix[role.key] || {},
        });
        return { roleKey: role.key, permissions: data.permissions || {} };
      }));
      const successful = results.filter(result => result.status === 'fulfilled').map(result => result.value);
      const failed = results.filter(result => result.status === 'rejected');
      setMatrix(prev => successful.reduce((next, result) => ({ ...next, [result.roleKey]: result.permissions }), prev));
      setDirtyRoles(prev => successful.reduce((next, result) => ({ ...next, [result.roleKey]: false }), prev));
      if (failed.length) {
        const message = failed[0].reason?.response?.data?.error || 'No se pudieron guardar todos los permisos';
        setError(message);
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 2200);
      }
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = roles.filter(role => !role.locked && dirtyRoles[role.key]).length;

  return (
    <div className="permissions-page">
      <div className="page-header permissions-header">
        <div>
          <h1 className="page-title">Permisos</h1>
          <p className="page-subtitle">Matriz de acceso por rol, módulo y sección.</p>
        </div>
        <button className="btn btn-primary" disabled={!apiReady || !pendingCount || saving} onClick={saveChanges}>
          {saving ? 'Guardando cambios...' : saved ? 'Cambios guardados' : 'Guardar cambios'}
        </button>
      </div>

      {error && <div className="alert alert-error permissions-alert">{error}</div>}

      {loading ? (
        <div className="flex items-center gap-8 text-dim">
          <div className="spinner" style={{ width: 20, height: 20 }} /> Cargando...
        </div>
      ) : (
        <div className="permissions-shell">
          <div className="permissions-table-wrap">
            <table className="permissions-table">
              <thead>
                <tr>
                  <th>Módulo</th>
                  <th>Sección</th>
                  {roles.map(role => <th key={role.key}>{role.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {groupedResources.map(group => group.resources.map((resource, index) => (
                  <tr key={resource.key}>
                    {index === 0 && (
                      <td className="permissions-module" rowSpan={group.resources.length}>
                        {group.module}
                      </td>
                    )}
                    <td>
                      <div className="permissions-section">{resource.section}</div>
                      <div className="permissions-path">{resource.path}</div>
                    </td>
                    {roles.map(role => (
                      <td key={`${role.key}-${resource.key}`}>
                        <div className="permissions-segmented" role="group" aria-label={`${role.label} ${resource.section}`}>
                          {LEVELS.map(level => {
                            const active = (matrix[role.key]?.[resource.key] || 'none') === level.value;
                            return (
                              <button
                                key={level.value}
                                type="button"
                                className={`permissions-option${active ? ' active' : ''}`}
                                disabled={role.locked}
                                onClick={() => setPermission(role.key, resource.key, level.value)}
                                title={level.label}
                              >
                                {level.shortLabel}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    ))}
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
